/**
 * ProServices.jsx
 * Route: /pro/services
 *
 * Service management for Pro accounts.
 * Uses the shared /api/services endpoints (account-isolated, ACCOUNT_OWNER role).
 *
 * Fields: name, description, duration, processing_time, default_price,
 *         requires_patch_test, allowed_days, recommended_interval_weeks
 *
 * recommended_interval_weeks drives the rebook suggestion at checkout.
 * NULL = no suggestion shown (all week buttons equal weight).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Chip, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Checkbox, Stack, Alert,
  CircularProgress, Divider, ToggleButton, ToggleButtonGroup,
  InputAdornment,
} from '@mui/material';
import AddIcon            from '@mui/icons-material/Add';
import EditIcon           from '@mui/icons-material/Edit';
import DeleteIcon         from '@mui/icons-material/Delete';
import ScienceIcon        from '@mui/icons-material/Science';
import EventRepeatIcon    from '@mui/icons-material/EventRepeat';
import ContentCutIcon     from '@mui/icons-material/ContentCut';
import api from './axiosConfig';

const NAVY = '#2f3542';
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = { mon: 'M', tue: 'T', wed: 'W', thu: 'T', fri: 'F', sat: 'S', sun: 'S' };
const DAY_FULL   = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const ALL_DAYS   = [...DAYS];

const emptyForm = () => ({
  name:                       '',
  description:                '',
  duration:                   30,
  processing_time:            0,
  default_price:              '',
  requires_patch_test:        false,
  allowed_days:               [...ALL_DAYS],
  recommended_interval_weeks: '',
});

// ── Helpers ────────────────────────────────────────────────────────────────

const fmtDuration = (mins) => {
  if (!mins) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const allDaysSelected = (days) =>
  days && DAYS.every(d => days.includes(d));

// ── Service Dialog ─────────────────────────────────────────────────────────

function ServiceDialog({ open, onClose, onSaved, existing }) {
  const isEdit = !!existing;
  const [form,   setForm]   = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  useEffect(() => {
    if (!open) return;
    if (isEdit && existing) {
      setForm({
        name:                       existing.name                       || '',
        description:                existing.description                || '',
        duration:                   existing.duration                   || 30,
        processing_time:            existing.processing_time            || 0,
        default_price:              existing.default_price != null ? String(existing.default_price) : '',
        requires_patch_test:        existing.requires_patch_test        || false,
        allowed_days:               existing.allowed_days?.length
                                      ? existing.allowed_days
                                      : [...ALL_DAYS],
        recommended_interval_weeks: existing.recommended_interval_weeks != null
                                      ? String(existing.recommended_interval_weeks)
                                      : '',
      });
    } else {
      setForm(emptyForm());
    }
    setError(null);
  }, [open, existing]);

  const set = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleDayToggle = (_, newDays) => {
    // MUI ToggleButtonGroup passes the new array
    set('allowed_days', newDays.length ? newDays : [...ALL_DAYS]);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Service name is required'); return; }
    if (!form.duration || parseInt(form.duration) < 5) {
      setError('Duration must be at least 5 minutes'); return;
    }

    const riw = form.recommended_interval_weeks !== ''
      ? parseInt(form.recommended_interval_weeks)
      : null;
    if (riw !== null && (riw < 1 || riw > 52)) {
      setError('Rebook interval must be between 1 and 52 weeks'); return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name:                       form.name.trim(),
        description:                form.description.trim(),
        duration:                   parseInt(form.duration),
        processing_time:            parseInt(form.processing_time) || 0,
        default_price:              parseFloat(form.default_price) || 0,
        requires_patch_test:        form.requires_patch_test,
        allowed_days:               allDaysSelected(form.allowed_days) ? null : form.allowed_days,
        recommended_interval_weeks: riw,
      };

      if (isEdit) {
        await api.put(`/services/${existing.id}`, payload);
      } else {
        await api.post('/services', payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const totalTime = (parseInt(form.duration) || 0) + (parseInt(form.processing_time) || 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEdit ? 'Edit Service' : 'New Service'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>

          <TextField
            fullWidth label="Service name *"
            value={form.name}
            onChange={e => set('name', e.target.value)}
          />

          <TextField
            fullWidth label="Description" multiline rows={2}
            value={form.description}
            onChange={e => set('description', e.target.value)}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth label="Active work duration (min) *" type="number"
              value={form.duration}
              onChange={e => set('duration', e.target.value)}
              inputProps={{ min: 5, step: 5 }}
              helperText="Time spent actively working"
            />
            <TextField
              fullWidth label="Processing time (min)" type="number"
              value={form.processing_time}
              onChange={e => set('processing_time', e.target.value)}
              inputProps={{ min: 0, step: 5 }}
              helperText="e.g. colour development"
            />
          </Stack>

          {(parseInt(form.duration) > 0 || parseInt(form.processing_time) > 0) && (
            <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
              Total time blocked: <strong>{fmtDuration(totalTime)}</strong>
              {parseInt(form.processing_time) > 0 && (
                <> · Active work: {fmtDuration(parseInt(form.duration) || 0)}
                 · Processing: {fmtDuration(parseInt(form.processing_time) || 0)}</>
              )}
            </Alert>
          )}

          <TextField
            fullWidth label="Default price (£)" type="number"
            value={form.default_price}
            onChange={e => set('default_price', e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
            helperText="Can be adjusted per appointment"
          />

          <Divider />

          <FormControlLabel
            control={
              <Checkbox
                checked={form.requires_patch_test}
                onChange={e => set('requires_patch_test', e.target.checked)}
                color="warning"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Requires patch test (chemical services)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Clients will need a valid patch test before this service is booked
                </Typography>
              </Box>
            }
          />

          <Divider />

          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Available online on
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Online clients can only book on selected days. Manual bookings are always unrestricted.
            </Typography>
            <ToggleButtonGroup
              value={form.allowed_days}
              onChange={handleDayToggle}
              size="small"
              sx={{ flexWrap: 'wrap', gap: 0.5 }}
            >
              {DAYS.map(d => (
                <ToggleButton
                  key={d} value={d}
                  sx={{
                    minWidth: 42, fontWeight: 600,
                    '&.Mui-selected': { bgcolor: NAVY, color: '#fff',
                      '&:hover': { bgcolor: '#1e2530' } },
                  }}
                >
                  {DAY_FULL[d]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {allDaysSelected(form.allowed_days) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Available every day online.
              </Typography>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Rebook interval (weeks)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Suggested rebooking interval shown at checkout. Leave blank for no suggestion.
              e.g. 6 for a colour service, 4 for a regular cut.
            </Typography>
            <TextField
              label="Weeks" type="number"
              value={form.recommended_interval_weeks}
              onChange={e => set('recommended_interval_weeks', e.target.value)}
              inputProps={{ min: 1, max: 52, step: 1 }}
              sx={{ width: 140 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">
                  <Typography variant="caption" color="text.secondary">wks</Typography>
                </InputAdornment>,
              }}
              placeholder="e.g. 6"
            />
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">Cancel</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#1e2530' } }}
        >
          {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete confirmation dialog ─────────────────────────────────────────────

function DeleteDialog({ open, onClose, onConfirm, service, deleting }) {
  if (!service) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete service?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Are you sure you want to delete <strong>{service.name}</strong>?
          This cannot be undone. Services with active appointments cannot be deleted.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting} color="inherit">Cancel</Button>
        <Button
          onClick={onConfirm} color="error" variant="contained" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
        >
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Days display ───────────────────────────────────────────────────────────

function DaysChips({ allowedDays }) {
  if (!allowedDays || allDaysSelected(allowedDays)) {
    return <Typography variant="caption" color="text.secondary">All days</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
      {DAYS.map(d => (
        <Chip
          key={d} label={DAY_LABELS[d]} size="small"
          sx={{
            height: 20, fontSize: '0.65rem', minWidth: 20,
            bgcolor: allowedDays.includes(d) ? NAVY : '#f0f0f0',
            color: allowedDays.includes(d) ? '#fff' : '#aaa',
          }}
        />
      ))}
    </Box>
  );
}

// ── Main ProServices ───────────────────────────────────────────────────────

export default function ProServices() {
  const [services,      setServices]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [dialogOpen,    setDialogOpen]    = useState(false);
  const [editTarget,    setEditTarget]    = useState(null);
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [deleteError,   setDeleteError]   = useState(null);

  const loadServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/services');
      setServices(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadServices(); }, [loadServices]);

  const handleNew  = () => { setEditTarget(null); setDialogOpen(true); };
  const handleEdit = (svc) => { setEditTarget(svc); setDialogOpen(true); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/services/${deleteTarget.id}`);
      setDeleteTarget(null);
      await loadServices();
    } catch (e) {
      setDeleteError(e.response?.data?.error || e.message || 'Failed to delete service');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, md: 3 } }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ContentCutIcon sx={{ color: 'text.secondary' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Services</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your service menu, durations, prices and rebook intervals
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained" startIcon={<AddIcon />}
          onClick={handleNew}
          sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#1e2530' } }}
        >
          New Service
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {deleteError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>{deleteError}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : services.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <ContentCutIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>No services yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add your first service to start taking bookings.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}
            sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#1e2530' } }}>
            Add first service
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Service Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Processing</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Total Time</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Patch Test</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Available</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  <Tooltip title="Rebook interval shown at checkout">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EventRepeatIcon fontSize="small" />
                      Rebook
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map(svc => {
                const totalMins = (svc.duration || 0) + (svc.processing_time || 0);
                return (
                  <TableRow key={svc.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{svc.name}</Typography>
                      {svc.description && (
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', maxWidth: 200,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {svc.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={fmtDuration(svc.duration)} size="small"
                        variant="outlined" sx={{ fontSize: '0.75rem' }} />
                    </TableCell>
                    <TableCell>
                      {svc.processing_time > 0
                        ? <Chip label={fmtDuration(svc.processing_time)} size="small"
                            variant="outlined" sx={{ fontSize: '0.75rem' }} />
                        : <Typography variant="caption" color="text.secondary">None</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{fmtDuration(totalMins)}</Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={600}>
                        £{parseFloat(svc.default_price || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {svc.requires_patch_test
                        ? <Chip icon={<ScienceIcon sx={{ fontSize: '14px !important' }} />}
                            label="Required" size="small"
                            sx={{ bgcolor: '#fff3e0', color: '#BF360C', fontWeight: 600, fontSize: '0.7rem' }} />
                        : <Typography variant="caption" color="text.secondary">No</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      <DaysChips allowedDays={svc.allowed_days} />
                    </TableCell>
                    <TableCell>
                      {svc.recommended_interval_weeks
                        ? <Chip
                            icon={<EventRepeatIcon sx={{ fontSize: '14px !important' }} />}
                            label={`${svc.recommended_interval_weeks}w`}
                            size="small"
                            sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.75rem' }}
                          />
                        : <Typography variant="caption" color="text.secondary">—</Typography>
                      }
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(svc)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteTarget(svc)}
                          sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ServiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={loadServices}
        existing={editTarget}
      />

      <DeleteDialog
        open={!!deleteTarget}
        onClose={() => { setDeleteTarget(null); setDeleteError(null); }}
        onConfirm={handleDelete}
        service={deleteTarget}
        deleting={deleting}
      />

    </Box>
  );
}